import { Component, OnInit } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { saveAs } from "file-saver";
import * as moment from "moment";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { environment } from "src/environments/environment";
import { DateRangeModel } from "../../components/datepicker-range/datepicker-i18n.service";
import { LoaderService } from "../../loader/loader.service";
import { PermissionsService } from "../../services/permissions.service";
import { UsersService } from "../../users/users.service";
import { ItemTotalizer } from "./models/payment-result";
import { Payment } from "./models/payment";
import { ReconciliationRow } from "./models/reconciliation-row";
import { ChargesStoreService } from "./services/charges-store.service";
import { ChargesService } from "./services/charges.service";
import { EnterpriseService } from "../../settings/enterprise/enterprise.service";
export type TabFilter =
  | "rejeitado"
  | "erro"
  | "aguardando"
  | "aprovado"
  | "sincronizado"
  | "remarcado"
  | "recorrencia"
  | "cobranca-avulsa"
  | "devolvido"
  | "estornado"
  | "notificado";

@Component({
  selector: "app-charges",
  templateUrl: "./charges.component.html",
  styleUrls: ["./charges.component.scss"],
})
export class ChargesComponent implements OnInit {
  env;
  activeTab: TabFilter = "aguardando";
  // Totalizers
  total: ItemTotalizer = { value: 0, parcels: 0 };
  credit_card: ItemTotalizer = { value: 0, parcels: 0 };
  debit_card: ItemTotalizer = { value: 0, parcels: 0 };
  money: ItemTotalizer = { value: 0, parcels: 0 };
  pix: ItemTotalizer = { value: 0, parcels: 0 };
  chargeAreas: { descricao_area: string; codigo_area: string }[] = [];
  turnoSelecionado: "manha" | "tarde" | "noite";

  printReport: boolean = false;
  currentApprovedPayments: Payment[] = [];
  currentSynchronizedPayments: Payment[] = [];
  currentWaitApprovalsPayments: Payment[] = [];
  currentRescheduledPayments: Payment[] = [];
  currentRecurrencesPayments: Payment[] = [];
  currentErrorsPayments: Payment[] = [];
  currentRejectedPayments: Payment[] = [];
  currentReturnPayments: Payment[] = [];
  currentEstornadosPayments: Payment[] = [];
  currentNotifiedPayments: Payment[] = [];
  reportPayments: Payment[] = [];

  generatingReconciliation = false;

  private unsubscribe$ = new Subject();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public permissionsService: PermissionsService,
    public chargesStore: ChargesStoreService,
    private chargesService: ChargesService,
    private loaderService: LoaderService,
    private usersService: UsersService,
    private enterpriseService: EnterpriseService,
    private modalService: NgbModal
  ) {
    this.env = environment;
  }

  ngOnInit() {
    this.route.params.pipe(takeUntil(this.unsubscribe$)).subscribe(async params => {
      if (this.permissionsService.isPermitted("cobranca")) {
        this.initializeComponent();
      } else {
        this.router.navigateByUrl(`/dash/${params.cnpj}`);
      }
    });
  }

  // Com o filtro por filial desabilitado a consulta abrange o grupo de
  // empresas, por isso o cobrador é obrigatório para delimitar o resultado.
  // Vale para a grade e para o Relatório de Recebimentos.
  //
  // Só `filial === false` conta como desabilitado: na abertura da tela o filtro
  // ainda não tem a chave (o default do store não a traz e setInitialFilters
  // resolve depois de um roundtrip), e nesse estado a consulta sai como
  // `filial=true` — filial logada, sem nada a bloquear nem a anunciar.
  canQueryPayments(): boolean {
    return this.chargesStore.paymentFilter.filial !== false || !!this.chargesStore.paymentFilter.cobrador;
  }

  async refreshData() {
    if (!this.canQueryPayments()) {
      return;
    }

    const limit = 10;
    const page = 1;

    const loader = await this.loaderService.notifica({
      title: "Buscando pagamentos...",
      date: moment().toISOString(),
      type: "loading",
    });

    await this.chargesStore.getPayments(this.env.selected.cnpj, limit, page);
    loader.dismiss();
    // await this.chargesStore.getSingleCharges(this.env.selected.cnpj, limit, page);
  }

  async initializeComponent() {
    this.setInitialFilters();
    this.getActiveTabState();
    await this.setDropdowns();
    this.setTotalizersObservable();
    await this.refreshData();
  }

  async setInitialFilters() {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    try {
      const enterprise = await this.enterpriseService.getEnterpriseById(this.env.selected._id).toPromise();
      const diaCiclo = (enterprise && enterprise.dia_ciclo_cobranca) || 1;
      firstDayOfMonth.setDate(diaCiclo);
    } catch (error) {
      console.error("Erro ao buscar dia_ciclo_cobranca:", error);
    }

    this.chargesStore.paymentFilter = {
      dt_recebimento: { fromDate: firstDayOfMonth, toDate: lastDayOfMonth },
      dt_agenda: { fromDate: null, toDate: null },
      contrato: [],
      cobrador: "",
      filial: true,
      tipoPayment: "Recebimento",
    };
  }

  getActiveTabState() {
    this.activeTab = this.chargesStore.tabFilter;
  }

  async onChangeActiveTab(tabFilter: TabFilter) {
    this.chargesStore.tabFilter = tabFilter;
    await this.refreshData();
  }

  async setDropdowns() {
    this.chargesStore.collectors = await this.usersService.getUsersCollectors(this.env.selected.cnpj).toPromise();
    this.chargesStore.operators = await this.usersService.getUsers(this.env.selected.cnpj).toPromise();
  }
  setTurnoRecebimento(turno: "manha" | "tarde" | "noite") {
    this.turnoSelecionado = turno;
    this.chargesStore.paymentFilter = {
      ...this.chargesStore.paymentFilter,
      turnoRecebimento: turno,
    };
  }
  setTotalizersObservable() {
    this.chargesStore.waitApprovalsPayments$.pipe(takeUntil(this.unsubscribe$)).subscribe(paymentResult => {
      this.total = paymentResult.total;
      this.credit_card = paymentResult.credit_card;
      this.debit_card = paymentResult.debit_card;
      this.money = paymentResult.money;
      this.pix = paymentResult.pix;
      this.currentWaitApprovalsPayments = paymentResult.result || [];
      this.updateReportPayments();
    });

    this.chargesStore.approvedPayments$.pipe(takeUntil(this.unsubscribe$)).subscribe(paymentResult => {
      this.total = paymentResult.total;
      this.credit_card = paymentResult.credit_card;
      this.debit_card = paymentResult.debit_card;
      this.money = paymentResult.money;
      this.pix = paymentResult.pix;
      this.currentApprovedPayments = paymentResult.result || [];
      this.updateReportPayments();
    });

    this.chargesStore.synchronizedPayments$.pipe(takeUntil(this.unsubscribe$)).subscribe(paymentResult => {
      this.total = paymentResult.total;
      this.credit_card = paymentResult.credit_card;
      this.debit_card = paymentResult.debit_card;
      this.money = paymentResult.money;
      this.pix = paymentResult.pix;
      this.currentSynchronizedPayments = paymentResult.result || [];
      this.updateReportPayments();
    });

    this.chargesStore.rescheduledPayments$.pipe(takeUntil(this.unsubscribe$)).subscribe(paymentResult => {
      this.total = paymentResult.total;
      this.credit_card = paymentResult.credit_card;
      this.debit_card = paymentResult.debit_card;
      this.money = paymentResult.money;
      this.pix = paymentResult.pix;
      this.currentRescheduledPayments = paymentResult.result || [];
      this.updateReportPayments();
    });

    this.chargesStore.recurrencesPayments$.pipe(takeUntil(this.unsubscribe$)).subscribe(paymentResult => {
      this.total = paymentResult.total;
      this.credit_card = paymentResult.credit_card;
      this.debit_card = paymentResult.debit_card;
      this.money = paymentResult.money;
      this.pix = paymentResult.pix;
      this.currentRecurrencesPayments = paymentResult.result || [];
      this.updateReportPayments();
    });

    this.chargesStore.errorsPayments$.pipe(takeUntil(this.unsubscribe$)).subscribe(paymentResult => {
      this.total = paymentResult.total;
      this.credit_card = paymentResult.credit_card;
      this.debit_card = paymentResult.debit_card;
      this.money = paymentResult.money;
      this.pix = paymentResult.pix;
      this.currentErrorsPayments = paymentResult.result || [];
      this.updateReportPayments();
    });

    this.chargesStore.rejectedPayments$.pipe(takeUntil(this.unsubscribe$)).subscribe(paymentResult => {
      this.total = paymentResult.total;
      this.credit_card = paymentResult.credit_card;
      this.debit_card = paymentResult.debit_card;
      this.money = paymentResult.money;
      this.pix = paymentResult.pix;
      this.currentRejectedPayments = paymentResult.result || [];
      this.updateReportPayments();
    });

    this.chargesStore.returnPayments$.pipe(takeUntil(this.unsubscribe$)).subscribe(paymentResult => {
      this.total = paymentResult.total;
      this.credit_card = paymentResult.credit_card;
      this.debit_card = paymentResult.debit_card;
      this.money = paymentResult.money;
      this.pix = paymentResult.pix;
      this.currentReturnPayments = paymentResult.result || [];
      this.updateReportPayments();
    });

    this.chargesStore.estornadosPayments$.pipe(takeUntil(this.unsubscribe$)).subscribe(paymentResult => {
      this.total = paymentResult.total;
      this.credit_card = paymentResult.credit_card;
      this.debit_card = paymentResult.debit_card;
      this.money = paymentResult.money;
      this.pix = paymentResult.pix;
      this.currentEstornadosPayments = paymentResult.result || [];
      this.updateReportPayments();
    });

    this.chargesStore.notifiedPayments$.pipe(takeUntil(this.unsubscribe$)).subscribe(paymentResult => {
      this.total = paymentResult.total;
      this.credit_card = paymentResult.credit_card;
      this.debit_card = paymentResult.debit_card;
      this.money = paymentResult.money;
      this.pix = paymentResult.pix;
      this.currentNotifiedPayments = paymentResult.result || [];
      this.updateReportPayments();
    });
  }

  goBack(): void {
    this.router.navigateByUrl(`/chargesmodule/${this.env.selected.cnpj}`);
  }

  // Métodos para o relatório de recebimentos
  private updateReportPayments(): void {
    const payments: Payment[] = [];
    payments.push(
      ...this.currentApprovedPayments,
      ...this.currentSynchronizedPayments,
      ...this.currentWaitApprovalsPayments,
      ...this.currentErrorsPayments
    );
    const uniqueIds = new Set(payments.map(p => p._id));

    if (uniqueIds.size === payments.length) {
      this.reportPayments = payments;
    } else {
      this.reportPayments = payments.filter(
        (payment, index, self) => index === self.findIndex(p => p._id === payment._id)
      );
    }
  }

  async loadAllDataForReport(): Promise<void> {
    const loader = await this.loaderService.notifica({
      title: "Carregando dados do relatório...",
      date: moment().toISOString(),
      type: "loading",
    });

    try {
      const statusList: TabFilter[] = [
        "aprovado",
        "sincronizado",
        "aguardando",
        "erro",
      ];

      await Promise.all(
        statusList.map(async (status) => {
          const payments = await this.chargesService
            .getPayments(this.env.selected.cnpj, status, this.chargesStore.paymentFilter, 5000, 1)
            .toPromise();

          if (payments) {
            const result = payments.result || [];
            if (status === "aguardando") this.currentWaitApprovalsPayments = result;
            if (status === "aprovado") this.currentApprovedPayments = result;
            if (status === "sincronizado") this.currentSynchronizedPayments = result;
            if (status === "erro") this.currentErrorsPayments = result;
          }
        })
      );

      this.updateReportPayments();
    } catch (error) {
      console.error("Erro ao carregar dados do relatório:", error);
    } finally {
      loader.dismiss();
    }
  }

  getAllPaymentsForReport(): Payment[] {
    return this.reportPayments;
  }

  getReportPeriod(): { inicio: Date; fim: Date } {
    const filter = this.chargesStore.paymentFilter;
    const inicio = filter.dt_recebimento.fromDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    const fim = filter.dt_recebimento.toDate || new Date();

    const inicioComHora = new Date(inicio);
    inicioComHora.setHours(0, 0, 0, 0);

    const fimComHora = new Date(fim);
    fimComHora.setHours(23, 59, 59, 999);

    return {
      inicio: inicioComHora,
      fim: fimComHora,
    };
  }

  printRecebimentosReport(): void {
    this.printReport = true;
  }

  async openReportModal(modalTemplate: any): Promise<void> {
    // Mesma regra da consulta da grade: sem cobrador o relatório abrangeria
    // todo o grupo de empresas, então não é gerado.
    if (!this.canQueryPayments()) {
      await this.loaderService.notifica({
        title: "Filtro por filial desabilitado. Selecione um Cobrador para gerar o relatório.",
        date: moment().toISOString(),
        type: "error",
      });
      return;
    }

    // Garantir que os coletores estejam carregados antes de abrir o relatório
    if (!this.chargesStore.collectors || this.chargesStore.collectors.length === 0) {
      await this.setDropdowns();
    }

    await this.loadAllDataForReport();

    this.modalService.open(modalTemplate, { size: "xl" });
  }

  reconciliationFromDate: Date;
  reconciliationToDate: Date;
  reconciliationAllBranches = false;
  reconciliationRangeError: string | null = null;
  private reconciliationModalRef: NgbModalRef | null = null;

  onReconciliationDateRangeChange(range: DateRangeModel): void {
    this.reconciliationFromDate = range.fromDate;
    this.reconciliationToDate = range.toDate;
    this.validateReconciliationRange();
  }

  isReconciliationValid(): boolean {
    return (
      !!this.reconciliationFromDate &&
      !!this.reconciliationToDate &&
      !this.reconciliationRangeError
    );
  }

  private validateReconciliationRange(): void {
    this.reconciliationRangeError = null;
    if (!this.reconciliationFromDate || !this.reconciliationToDate) return;
    const diff = moment(this.reconciliationToDate).diff(moment(this.reconciliationFromDate), "days");
    if (diff < 0) {
      this.reconciliationRangeError = "Data inicial deve ser anterior à final.";
    } else if (diff > 90) {
      this.reconciliationRangeError = "Período máximo permitido: 3 meses (90 dias).";
    }
  }

  openReconciliationModal(template: any): void {
    this.reconciliationFromDate = undefined;
    this.reconciliationToDate = undefined;
    this.reconciliationAllBranches = false;
    this.reconciliationRangeError = null;
    this.reconciliationModalRef = this.modalService.open(template, {
      size: "xl",
      centered: true,
      windowClass: "reconciliation-modal",
    });
  }

  async submitReconciliation(): Promise<void> {
    if (!this.isReconciliationValid() || this.generatingReconciliation) return;

    this.generatingReconciliation = true;
    const loader = await this.loaderService.notifica({
      title: "Gerando conciliação...",
      date: moment().toISOString(),
      type: "loading",
    });

    try {
      const rows = await this.chargesService
        .getReconciliationExport(this.env.selected.cnpj, {
          dt_inicio: moment(this.reconciliationFromDate).format("YYYY-MM-DD"),
          dt_fim: moment(this.reconciliationToDate).format("YYYY-MM-DD"),
          todas_filiais: this.reconciliationAllBranches,
        })
        .toPromise();

      if (!rows || rows.length === 0) {
        await this.loaderService.notifica({
          title: "Nenhum dado encontrado para o período selecionado.",
          date: moment().toISOString(),
          type: "error",
        });
        return;
      }

      const csv = this.buildReconciliationCsv(rows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      saveAs(blob, `conciliacao-${moment().format("YYYY-MM-DD-HHmmss")}.csv`);

      if (this.reconciliationModalRef) {
        this.reconciliationModalRef.close();
        this.reconciliationModalRef = null;
      }
    } catch (error) {
      console.error("Erro ao gerar conciliação:", error);
      await this.loaderService.notifica({
        title: "Erro ao gerar conciliação.",
        date: moment().toISOString(),
        type: "error",
      });
    } finally {
      loader.dismiss();
      this.generatingReconciliation = false;
    }
  }

  private buildReconciliationCsv(rows: ReconciliationRow[]): string {
    const header = "id;chave;data_baixa;cobrador;valor;desconto;status_processamento";
    const formatDate = (v: any) => {
      if (!v) return "";
      const d = v instanceof Date ? v : new Date(v);
      if (isNaN(d.getTime())) return "";
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}${m}${day}`;
    };
    const formatMoney = (n: any) => (Number(n) || 0).toFixed(2).replace(".", ",");
    const escape = (v: any) => {
      const s = v == null ? "" : String(v);
      return /[;"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const lines = [header];
    for (const r of rows) {
      lines.push(
        [
          escape(r.id),
          escape(r.chave),
          escape(formatDate(r.data_baixa)),
          escape(r.cobrador),
          escape(formatMoney(r.valor)),
          escape(formatMoney(r.desconto)),
          escape(r.status_processamento),
        ].join(";")
      );
    }
    return lines.join("\n") + "\n";
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
